/**
 * Join (slice 6a §6.3, artboards 73, 73b, 73c): outside the shell, as login
 * is, and with no session needed. The invite's workspace, who invited them,
 * the crew's avatars (the Chief of Staff's first), then their name and a
 * password; Join the crew signs them in and lands on Chats. An expired,
 * spent or unknown link says "This invite has expired".
 */
import { Button } from "@crew/ui/components/button";
import { AgentAvatar, PersonAvatar } from "@crew/ui/components/crew-avatar";
import { Input } from "@crew/ui/components/input";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError, errorHint } from "../../app/api";
import { personTint } from "../../app/tints";
import { startSession } from "../auth/logout";
import { useJoin, useJoinView, type JoinView } from "./api";
import { crewLine } from "./people-view";
import { HOME } from "../../app/home";

const PASSWORD_MIN = 10;
const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

function Mark({ withName = true }: { withName?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex size-7 items-center justify-center rounded-nav bg-foreground font-display text-question leading-5 text-background">
        C
      </span>
      {withName ? (
        <span className="font-display text-[22px] leading-6.5 text-foreground">Crew</span>
      ) : null}
    </div>
  );
}

function Page({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-svh flex-col bg-background px-6 pt-12 pb-6 md:items-center md:justify-center md:p-6">
      <div className="flex w-full flex-col md:w-110">{children}</div>
    </div>
  );
}

export function JoinPage() {
  const { token = "" } = useParams();
  const view = useJoinView(token);
  if (view.isPending) {
    return (
      <Page>
        <Spinner className="size-5 self-center text-muted-foreground" />
      </Page>
    );
  }
  if (view.error) {
    const gone =
      view.error instanceof ApiError && (view.error.status === 404 || view.error.status === 410);
    if (!gone) {
      return (
        <Page>
          <p className="text-body text-muted-foreground">
            {errorHint(view.error)}{" "}
            <Button variant="link" onClick={() => void view.refetch()}>
              Try again
            </Button>
          </p>
        </Page>
      );
    }
    return <Expired error={view.error as ApiError} />;
  }
  return <JoinForm token={token} view={view.data} />;
}

/** 73c: expired, spent, cancelled or unknown, all the same to the person. */
function Expired({ error }: { error: ApiError }) {
  const workspace = (error.body.workspace as { name?: string } | undefined)?.name;
  const inviter = (error.body.invited_by as { name?: string } | undefined)?.name;
  const line =
    workspace && inviter
      ? `Invites to ${workspace} work for 7 days, and once. Ask ${firstName(inviter)} to send you a new one from People.`
      : "Invites work for 7 days, and once. Ask whoever invited you to send you a new one from People.";
  return (
    <Page>
      <div className="flex flex-col gap-7" data-join-expired>
        <Mark withName={false} />
        <div className="flex flex-col gap-2.5">
          <h1 className="font-display text-[38px] leading-10 tracking-tight text-foreground md:text-[44px] md:leading-11.5">
            This invite has expired
          </h1>
          <p className="text-body leading-5.5 text-copy">{line}</p>
        </div>
        <Link
          href="/login"
          className="flex h-11 items-center justify-center rounded-md border border-input bg-card text-body font-medium text-foreground"
        >
          Already joined? Sign in
        </Link>
      </div>
    </Page>
  );
}

function Crew({ people }: { people: JoinView["people"] }) {
  const phone = useIsMobile();
  return (
    <div className="flex items-center gap-3 pt-3.5">
      <div className="flex">
        <span className="overflow-hidden rounded-full border-2 border-background">
          <AgentAvatar glyph="C" tint="ink" size={26} />
        </span>
        {people.map((p) => (
          <span key={`${p.name}-${p.initials}`} className="rounded-full border-2 border-background">
            <PersonAvatar initials={p.initials} tint={personTint(p.tint)} size={26} />
          </span>
        ))}
      </div>
      <span className="text-meta text-copy">{crewLine(people, phone)}</span>
    </div>
  );
}

function JoinForm({ token, view }: { token: string; view: JoinView }) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const join = useJoin(token);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const ready = name.trim().length > 0 && password.length >= PASSWORD_MIN && !join.isPending;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    join.mutate(
      { name: name.trim(), password },
      {
        onSuccess: async () => {
          await startSession(client);
          void navigate(HOME, { replace: true });
        },
      },
    );
  };
  // Spent or expired since the page opened: the same page as on arrival.
  if (join.error instanceof ApiError && [404, 410].includes(join.error.status)) {
    return <Expired error={join.error} />;
  }
  return (
    <Page>
      <JoinHeading view={view} />
      <form onSubmit={submit} className="flex flex-col" data-join-form>
        <JoinFields
          email={view.email}
          name={name}
          password={password}
          onName={setName}
          onPassword={setPassword}
        />
        <div className="flex flex-col gap-3.5 pt-6">
          {join.error ? (
            <p className="text-meta text-destructive" data-join-error>
              {errorHint(join.error)}
            </p>
          ) : null}
          <Button type="submit" size="lg" className="h-11 text-body" disabled={!ready}>
            {join.isPending ? "Joining…" : "Join the crew"}
          </Button>
          <p className="text-meta leading-sm text-muted-foreground">
            Not you? Ask {firstName(view.invited_by.name)} to invite another address. By joining you
            agree to Crew's terms and privacy policy.
          </p>
        </div>
      </form>
    </Page>
  );
}

function JoinHeading({ view }: { view: JoinView }) {
  return (
    <>
      <div className="flex flex-col gap-7">
        <Mark />
        <div className="flex flex-col gap-2.5">
          <h1 className="font-display text-[38px] leading-10 tracking-tight text-foreground md:text-[44px] md:leading-11.5">
            Join {view.workspace.name}
          </h1>
          <p className="text-body leading-5.5 text-muted-foreground">
            {view.invited_by.name} invited you. Choose a password, and you're in.
          </p>
        </div>
      </div>
      <Crew people={view.people} />
    </>
  );
}

const FIELD = "h-10.5 rounded-md border-input bg-card px-3 text-body md:text-body";

/** The email the invite fixed, their name, and a password. */
function JoinFields({
  email,
  name,
  password,
  onName,
  onPassword,
}: {
  email: string;
  name: string;
  password: string;
  onName: (name: string) => void;
  onPassword: (password: string) => void;
}) {
  return (
    <div className="flex flex-col gap-4 pt-6 md:pt-7">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="join-email" className="text-meta font-medium text-foreground">
          Email
        </label>
        <div className="flex h-10.5 items-center justify-between rounded-md border border-border bg-muted px-3">
          <span id="join-email" className="truncate text-body text-copy">
            {email}
          </span>
          <Lock
            className="size-3.5 shrink-0 text-hint"
            strokeWidth={1.8}
            aria-label="Fixed by the invite"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="join-name" className="text-meta font-medium text-foreground">
          Your name
        </label>
        <Input
          id="join-name"
          autoComplete="name"
          placeholder="Your name"
          maxLength={80}
          required
          value={name}
          onChange={(e) => onName(e.target.value)}
          className={FIELD}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline gap-1.5">
          <label htmlFor="join-password" className="text-meta font-medium text-foreground">
            Password
          </label>
          <span className="text-meta text-hint">At least 10 characters</span>
        </div>
        <Input
          id="join-password"
          type="password"
          autoComplete="new-password"
          minLength={PASSWORD_MIN}
          required
          value={password}
          onChange={(e) => onPassword(e.target.value)}
          className={FIELD}
        />
      </div>
    </div>
  );
}
